/**
 * ConversationMembersButton — the simple view's way to the roster (conversation-members spec): a header button naming
 * how many members the conversation has, and how many are working, that opens the roster in a side panel. Demo page:
 * /chats (the header's people control); the panel is the shared `Sheet`.
 */
import { Users } from 'lucide-react';
import { useState } from 'react';
import { useConversationMembers } from '../../hooks/useConversationMembers.js';
import type { StreamMessage } from '../../hooks/useConversationStream.js';
import { Button } from '../ui/Button.js';
import { Sheet } from '../ui/Sheet.js';
import { workingMemberIds } from './conversation-members-model.js';
import { ConversationRoster } from './ConversationRoster.js';

interface ConversationMembersButtonProps {
  conversationId: string;
  messages: readonly StreamMessage[];
  /** Opens a member's thread in place of this conversation (the panel closes). */
  onOpenConversation?: (conversationId: string) => void;
}

export function ConversationMembersButton({
  conversationId,
  messages,
  onOpenConversation,
}: ConversationMembersButtonProps) {
  const [open, setOpen] = useState(false);
  const { members, me, threads } = useConversationMembers(conversationId, messages);
  const working = workingMemberIds(messages);
  const busy = members.filter((member) => working.has(member.id)).length;
  const label = `${members.length} ${members.length === 1 ? 'member' : 'members'}${busy > 0 ? ` · ${busy} working` : ''}`;
  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)} aria-label={`Members: ${label}`}>
        <Users size={14} aria-hidden />
        {label}
      </Button>
      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title="Members"
        description="Only the members a message is addressed to receive it."
        width="sm"
      >
        <ConversationRoster
          conversationId={conversationId}
          members={members}
          me={me}
          working={working}
          threads={threads}
          {...(onOpenConversation
            ? {
                onOpenConversation: (threadId: string) => {
                  setOpen(false);
                  onOpenConversation(threadId);
                },
              }
            : {})}
        />
      </Sheet>
    </>
  );
}
